import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Nav, Dropdown } from 'react-bootstrap';
import { NavLink, Link, useHistory, useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';

const TenantManagementLayout = ({ auth, children, title = 'Tenant Management', subtitle }) => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [tenants, setTenants] = useState([]);
  const hasFetchedTenants = useRef(false);

  useEffect(() => {
    if (auth === null || auth === undefined) return;
    if (auth === false) {
      history.push('/');
      return;
    }
    if (!auth.id || !['super_admin', 'admin', 'manager'].includes(auth.platform_role)) {
      history.push('/');
    }
  }, [auth, history]);

  // Fetch all tenants once for the switcher
  useEffect(() => {
    if (hasFetchedTenants.current) return;

    const fetchTenants = async () => {
      try {
        const response = await fetch('/api/admin/tenants', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setTenants(data.tenants || []);
          hasFetchedTenants.current = true;
        }
      } catch (err) {
        console.error('Error fetching tenants:', err);
      }
    };

    if (auth && ['super_admin', 'admin', 'manager'].includes(auth.platform_role)) {
      fetchTenants();
    }
  }, [auth]);

  // Find current tenant from the cached list
  const currentTenant = tenants.find(t => t.id === id);

  // Get the URL for a tenant based on current page type
  const getTenantUrl = (tenantId) => {
    const path = location.pathname;
    if (path.includes('/tokens')) {
      return `/admin/tenants/${tenantId}/tokens`;
    } else if (path.includes('/users')) {
      return `/admin/tenants/${tenantId}/users`;
    }
    return `/admin/tenants/${tenantId}`;
  };

  if (!auth || !['super_admin', 'admin', 'manager'].includes(auth.platform_role)) {
    return null;
  }

  const navLinkStyle = {
    display: 'block',
    padding: '0.75rem 1rem',
    color: '#64748b',
    textDecoration: 'none',
    borderRadius: '8px',
    marginBottom: '0.25rem',
    transition: 'all 0.2s',
    fontSize: '0.95rem',
    fontWeight: '500'
  };

  const navLinkActiveStyle = {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white'
  };

  return (
    <>
      <Helmet>
        <title>{title} | Spawnry Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 3rem',
        marginBottom: '2rem'
      }}>
        <Container>
          <h1 style={{
            color: 'white',
            fontWeight: '700',
            fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
            marginBottom: '0.5rem'
          }}>
            {title}
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
            {subtitle || 'Manage tenants, users, and token allocations'}
          </p>
        </Container>
      </div>

      <Container style={{ paddingBottom: '3rem' }}>
        <Row>
          <Col md={3} className="mb-4">
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1rem',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
            }}>
              <Nav className="flex-column">
                <NavLink
                  to="/admin/tenants"
                  exact
                  style={navLinkStyle}
                  activeStyle={navLinkActiveStyle}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.75rem', verticalAlign: '-3px' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="9" y1="21" x2="9" y2="9"/>
                  </svg>
                  All Tenants
                </NavLink>
                {['super_admin', 'admin'].includes(auth.platform_role) && (
                  <NavLink
                    to="/admin/tenants/new"
                    style={navLinkStyle}
                    activeStyle={navLinkActiveStyle}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.75rem', verticalAlign: '-3px' }}>
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="16"/>
                      <line x1="8" y1="12" x2="16" y2="12"/>
                    </svg>
                    Create Tenant
                  </NavLink>
                )}
                <NavLink
                  to="/admin/impersonate"
                  style={navLinkStyle}
                  activeStyle={navLinkActiveStyle}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.75rem', verticalAlign: '-3px' }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Impersonation
                </NavLink>
                <hr style={{ margin: '0.5rem 0', borderColor: '#e2e8f0' }} />
                <p style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', margin: '0.75rem 0 0.5rem 0.5rem', fontWeight: '600', letterSpacing: '0.05em' }}>
                  Platform Admin
                </p>
                <NavLink
                  to="/admin/platform/users"
                  style={navLinkStyle}
                  activeStyle={navLinkActiveStyle}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.75rem', verticalAlign: '-3px' }}>
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Platform Users
                </NavLink>
                <NavLink
                  to="/admin/platform/articles"
                  style={navLinkStyle}
                  activeStyle={navLinkActiveStyle}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.75rem', verticalAlign: '-3px' }}>
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  Articles & Costs
                </NavLink>
                <NavLink
                  to="/admin/platform/models"
                  style={navLinkStyle}
                  activeStyle={navLinkActiveStyle}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.75rem', verticalAlign: '-3px' }}>
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  AI Models
                </NavLink>
                <NavLink
                  to="/admin/tools"
                  style={navLinkStyle}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.75rem', verticalAlign: '-3px' }}>
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                  </svg>
                  Tool Admin
                </NavLink>
              </Nav>
            </div>

            {/* Tenant Actions - Always visible */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1rem',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              marginTop: '1rem'
            }}>
              <p style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: '600' }}>
                Tenant Actions
              </p>

              {/* Tenant Switcher */}
              <Dropdown className="mb-3">
                <Dropdown.Toggle
                  variant="light"
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: id ? '#f8fafc' : '#fef3c7',
                    border: id ? '1px solid #e2e8f0' : '1px solid #fbbf24',
                    fontWeight: '500',
                    color: '#1e293b'
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentTenant?.name || 'Select a tenant...'}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ width: '100%', maxHeight: '300px', overflowY: 'auto' }}>
                  {tenants.length === 0 ? (
                    <Dropdown.Item disabled>Loading tenants...</Dropdown.Item>
                  ) : (
                    tenants.map(tenant => (
                      <Dropdown.Item
                        key={tenant.id}
                        as={Link}
                        to={getTenantUrl(tenant.id)}
                        active={tenant.id === id}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none' }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tenant.name}
                        </span>
                        <span style={{
                          fontSize: '0.7rem',
                          color: tenant.is_active ? '#16a34a' : '#dc2626',
                          marginLeft: '0.5rem',
                          flexShrink: 0
                        }}>
                          {tenant.is_active ? 'active' : 'inactive'}
                        </span>
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>

              {/* Show tenant actions only when a tenant is selected */}
              {id ? (
                <Nav className="flex-column">
                  <NavLink
                    to={`/admin/tenants/${id}`}
                    exact
                    style={navLinkStyle}
                    activeStyle={navLinkActiveStyle}
                  >
                    Details
                  </NavLink>
                  <NavLink
                    to={`/admin/tenants/${id}/tokens`}
                    style={navLinkStyle}
                    activeStyle={navLinkActiveStyle}
                  >
                    Token Management
                  </NavLink>
                  <NavLink
                    to={`/admin/tenants/${id}/users`}
                    style={navLinkStyle}
                    activeStyle={navLinkActiveStyle}
                  >
                    Users
                  </NavLink>
                </Nav>
              ) : (
                <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0, fontStyle: 'italic' }}>
                  Select a tenant to view actions
                </p>
              )}
            </div>
          </Col>

          <Col md={9}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.5rem',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              minHeight: '400px'
            }}>
              {children}
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TenantManagementLayout);
